import { useState, useEffect } from 'react';
import { involvementAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
    Gauge, Briefcase, ListTodo, MessageCircle, GraduationCap, Activity, Megaphone,
    LogIn, Repeat, Timer, UsersRound, ChevronDown,
    Sparkles, Loader2, ThumbsUp, ThumbsDown, AlertTriangle, Target, MessageSquare, Clock,
} from 'lucide-react';

// 6 sous-scores + les métriques qui les composent (déroulé au clic). Animation = managers.
const SUBS = [
    { key: 'crm', label: 'CRM', icon: Briefcase, metrics: [
        { k: 'active_dossiers', label: 'Dossiers actifs' },
        { k: 'couverture_pct', label: 'Couverture tâches', suffix: '%' },
        { k: 'securisation_pct', label: 'Sécurisés', suffix: '%' },
        { k: 'dossiers_non_securises', label: 'Non sécurisés', warnGt: 0 },
        { k: 'qualite_docs_pct', label: 'Qualité docs EP', suffix: '%' },
        { k: 'pipeline_frais_pct', label: 'Pipeline frais', suffix: '%' },
    ] },
    { key: 'discipline', label: 'Discipline', icon: ListTodo, metrics: [
        { k: 'taches_actives', label: 'Tâches actives' },
        { k: 'taches_en_retard', label: 'Tâches en retard', warnGt: 0 },
        { k: 'retard_pct', label: '% en retard', suffix: '%', warnGt: 0 },
        { k: 'taches_cloturees_fenetre', label: 'Clôturées (30j)' },
        { k: 'temps_moyen_sans_action_jours', label: 'Tps sans action', suffix: 'j', warnGt: 14 },
    ] },
    { key: 'reactivite', label: 'Réactivité', icon: MessageCircle, metrics: [
        { k: 'temps_moyen_sans_action_jours', label: 'Tps moyen sans action', suffix: 'j', warnGt: 14 },
    ] },
    { key: 'academie', label: 'Académie', icon: GraduationCap, metrics: [
        { k: 'academie_modules', label: 'Modules suivis' },
    ] },
    { key: 'inside_vnx', label: 'Inside VNX', icon: Activity, metrics: [
        { k: 'vnx_activite', label: 'Activité VNX' },
        { k: 'idees_proposees', label: 'Idées proposées' },
        { k: 'idees_adoptees', label: 'Idées adoptées' },
        { k: 'mur_commentaires_fenetre', label: 'Commentaires mur (30j)' },
        { k: 'jours_depuis_connexion_vnx', label: 'Jours / connexion VNX', suffix: 'j', warnGt: 7 },
    ] },
    { key: 'animation', label: 'Animation', icon: Megaphone, metrics: [
        { k: 'animation_activite_mur', label: 'Activité mur' },
        { k: 'animation_reactivite', label: 'Réactivité équipe' },
        { k: 'animation_relance', label: 'Relances' },
        { k: 'animation_impact_equipe', label: 'Impact équipe' },
    ] },
];
const isVPRole = (role) => role === 'vp' || role === 'admin' || role === 'admin_full';
const subsForRole = (role) => (isVPRole(role) ? SUBS : SUBS.filter((s) => s.key !== 'animation'));

const scoreColor = (s) => {
    if (s === null || s === undefined) return 'text-slate-400';
    if (s >= 85) return 'text-emerald-600';
    if (s >= 70) return 'text-green-600';
    if (s >= 50) return 'text-amber-600';
    if (s >= 30) return 'text-orange-600';
    return 'text-red-600';
};
const barColor = (s) => {
    if (s >= 85) return 'bg-emerald-500';
    if (s >= 70) return 'bg-green-500';
    if (s >= 50) return 'bg-amber-500';
    if (s >= 30) return 'bg-orange-500';
    return 'bg-red-500';
};
const labelBadge = (s) => {
    if (s === null || s === undefined) return 'bg-slate-100 text-slate-500';
    if (s >= 85) return 'bg-emerald-100 text-emerald-700';
    if (s >= 70) return 'bg-green-100 text-green-700';
    if (s >= 50) return 'bg-amber-100 text-amber-700';
    if (s >= 30) return 'bg-orange-100 text-orange-700';
    return 'bg-red-100 text-red-700';
};
const riskBadge = (r) => ({
    Faible: 'bg-emerald-100 text-emerald-700',
    Moyen: 'bg-amber-100 text-amber-700',
    'Élevé': 'bg-orange-100 text-orange-700',
    Critique: 'bg-red-100 text-red-700',
}[r] || 'bg-slate-100 text-slate-500');

const relDate = (iso, days) => {
    if (!iso || days === null || days === undefined) return 'Jamais';
    const d = new Date(iso);
    const dateStr = isNaN(d) ? '' : d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
    if (days <= 0) return "Aujourd'hui";
    if (days === 1) return dateStr ? `Hier (${dateStr})` : 'Hier';
    return dateStr ? `il y a ${days} j (${dateStr})` : `il y a ${days} j`;
};
const loginColor = (days) => {
    if (days === null || days === undefined) return 'text-slate-400';
    if (days <= 1) return 'text-emerald-600';
    if (days <= 3) return 'text-green-600';
    if (days <= 7) return 'text-amber-600';
    if (days <= 10) return 'text-orange-600';
    return 'text-red-600';
};

function ScoreRing({ value, size = 72 }) {
    const r = (size - 8) / 2;
    const c = 2 * Math.PI * r;
    const pct = value === null || value === undefined ? 0 : value;
    const off = c - (pct / 100) * c;
    const stroke = pct >= 70 ? '#16a34a' : pct >= 50 ? '#d97706' : pct >= 30 ? '#ea580c' : '#dc2626';
    return (
        <svg width={size} height={size} className="shrink-0">
            <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e2e8f0" strokeWidth="6" />
            <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={value === null ? '#cbd5e1' : stroke}
                strokeWidth="6" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off}
                transform={`rotate(-90 ${size / 2} ${size / 2})`} style={{ transition: 'stroke-dashoffset .6s ease' }} />
            <text x="50%" y="50%" dominantBaseline="central" textAnchor="middle"
                className={`text-base font-bold ${scoreColor(value)}`} fill="currentColor">
                {value === null || value === undefined ? '—' : Math.round(value)}
            </text>
        </svg>
    );
}

function SubScore({ sub, value, metrics, expanded, onToggle }) {
    const { icon: Icon, label } = sub;
    return (
        <div className="space-y-1" data-testid={`implication-subscore-${label}`}>
            <button type="button" onClick={onToggle}
                className="w-full flex items-center justify-between text-xs hover:opacity-80 transition-opacity"
                data-testid={`implication-subscore-toggle-${sub.key}`}>
                <span className="flex items-center gap-1 text-slate-600">
                    <Icon className="w-3.5 h-3.5" />{label}
                    <ChevronDown className={`w-3 h-3 text-slate-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
                </span>
                <span className={`font-semibold ${scoreColor(value)}`}>{value === null || value === undefined ? 'N/A' : Math.round(value)}</span>
            </button>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                <div className={`h-full rounded-full ${barColor(value || 0)}`} style={{ width: `${value || 0}%`, transition: 'width .5s' }} />
            </div>
            {expanded && (
                <div className="grid grid-cols-2 gap-1.5 pt-1.5" data-testid={`implication-subscore-detail-${sub.key}`}>
                    {sub.metrics.map((m) => {
                        const raw = (metrics || {})[m.k];
                        const val = raw === null || raw === undefined ? 'N/A' : `${raw}${m.suffix || ''}`;
                        const warn = m.warnGt !== undefined && typeof raw === 'number' && raw > m.warnGt;
                        return <Metric key={m.k} label={m.label} value={val} warn={warn} />;
                    })}
                </div>
            )}
        </div>
    );
}

function Metric({ label, value, warn }) {
    return (
        <div className={`rounded-lg border p-1.5 ${warn ? 'border-amber-200 bg-amber-50/60' : 'bg-slate-50'}`}>
            <p className="text-[10px] text-slate-500 leading-tight">{label}</p>
            <p className={`text-xs font-semibold ${warn ? 'text-amber-700' : 'text-slate-800'}`}>{value}</p>
        </div>
    );
}

function AiBlock({ icon: Icon, color, title, text }) {
    if (!text) return null;
    return (
        <div>
            <p className={`flex items-center gap-1.5 font-medium ${color}`}><Icon className="w-4 h-4" />{title}</p>
            <p className="text-slate-700 mt-0.5">{text}</p>
        </div>
    );
}

function ConnInfo({ metrics }) {
    if (!metrics) return null;
    const items = [
        { icon: LogIn, label: 'CRM', value: relDate(metrics.last_crm_login, metrics.jours_depuis_connexion_crm), color: loginColor(metrics.jours_depuis_connexion_crm) },
        { icon: Activity, label: 'Inside VNX', value: relDate(metrics.last_vnx_login, metrics.jours_depuis_connexion_vnx), color: loginColor(metrics.jours_depuis_connexion_vnx) },
        { icon: Repeat, label: 'Fréquence', value: metrics.frequence_moyenne_jours ? `tous les ${metrics.frequence_moyenne_jours} j` : 'N/A', color: 'text-slate-700' },
        { icon: Timer, label: 'Session moy.', value: metrics.temps_session_moyen_minutes != null ? `${metrics.temps_session_moyen_minutes} min` : 'N/A', color: 'text-slate-700' },
    ];
    return (
        <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 rounded-lg bg-slate-50 p-2.5" data-testid="implication-conn-info">
            {items.map((it) => (
                <div key={it.label} className="flex items-center gap-1.5 text-[11px]">
                    <it.icon className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                    <span className="text-slate-500 shrink-0">{it.label} :</span>
                    <span className={`font-semibold truncate ${it.color}`}>{it.value}</span>
                </div>
            ))}
        </div>
    );
}

/**
 * Fiche d'implication d'un utilisateur (note d'engagement + sous-scores + connexion).
 * Respecte les règles de visibilité côté backend : admin = tous, VP = son équipe,
 * apporteur = 403. En cas d'accès refusé/erreur, le composant ne s'affiche pas.
 */
export const UserImplicationCard = ({ userId }) => {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [allowed, setAllowed] = useState(true);
    const [expanded, setExpanded] = useState(null);
    const [aiLoading, setAiLoading] = useState(false);

    useEffect(() => {
        if (!userId) return;
        let active = true;
        setLoading(true);
        setExpanded(null);
        involvementAPI.userDetail(userId, 30)
            .then((res) => { if (active) { setDetail(res.data); setAllowed(true); } })
            .catch(() => { if (active) setAllowed(false); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [userId]);

    const runAi = async () => {
        setAiLoading(true);
        try {
            const r = await involvementAPI.aiAnalysis(userId, 30);
            setDetail((d) => ({ ...d, ai_analysis: r.data }));
            if (r.data?.ai_available === false) toast.error('IA momentanément indisponible');
            else toast.success('Synthèse IA générée');
        } catch { toast.error("Échec de l'analyse IA"); }
        finally { setAiLoading(false); }
    };

    if (!allowed) return null;

    if (loading) {
        return (
            <Card data-testid="user-implication-card">
                <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                        <Gauge className="w-5 h-5 text-slate-600" /> Fiche d'implication
                    </CardTitle>
                </CardHeader>
                <CardContent><Skeleton className="h-28 w-full" /></CardContent>
            </Card>
        );
    }
    if (!detail) return null;

    return (
        <Card data-testid="user-implication-card">
            <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                    <Gauge className="w-5 h-5 text-purple-600" /> Fiche d'implication
                    <span className="text-xs font-normal text-slate-400">(30 derniers jours)</span>
                </CardTitle>
            </CardHeader>
            <CardContent>
                <div className="flex flex-col sm:flex-row gap-5">
                    {/* Note globale */}
                    <div className="flex items-center gap-3 shrink-0">
                        <ScoreRing value={detail.global_score} />
                        <div className="space-y-1">
                            <Badge className={labelBadge(detail.global_score)} data-testid="implication-label">{detail.label}</Badge>
                            <div>
                                <Badge className={riskBadge(detail.risque_decrochage)} data-testid="implication-risk">
                                    Décrochage : {detail.risque_decrochage}
                                </Badge>
                            </div>
                            {isVPRole(detail.role) && detail.team_impact_score !== null && detail.team_impact_score !== undefined && (
                                <Badge variant="outline" className="text-[11px] flex items-center gap-1 w-fit">
                                    <UsersRound className="w-3 h-3" /> Impact équipe {detail.team_impact_score}
                                </Badge>
                            )}
                        </div>
                    </div>
                    {/* Sous-scores + connexion */}
                    <div className="flex-1 min-w-0 space-y-3">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-2">
                            {subsForRole(detail.role).map((s) => (
                                <SubScore key={s.key} sub={s} value={(detail.scores || {})[s.key]}
                                    metrics={detail.metrics} expanded={expanded === s.key}
                                    onToggle={() => setExpanded(expanded === s.key ? null : s.key)} />
                            ))}
                        </div>
                        <ConnInfo metrics={detail.metrics} />
                    </div>
                </div>

                {/* Synthèse IA */}
                <div className="mt-4 rounded-xl border bg-gradient-to-br from-violet-50 to-blue-50 p-4" data-testid="implication-ai-section">
                    <div className="flex items-center justify-between mb-3">
                        <p className="font-semibold flex items-center gap-2 text-violet-800">
                            <Sparkles className="w-4 h-4" /> Synthèse IA
                        </p>
                        <Button size="sm" variant="outline" onClick={runAi} disabled={aiLoading} data-testid="implication-generate-ai-btn">
                            {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (detail.ai_analysis ? 'Régénérer' : 'Générer')}
                        </Button>
                    </div>
                    {!detail.ai_analysis ? (
                        <p className="text-sm text-slate-500">Cliquez sur « Générer » pour obtenir une synthèse personnalisée de cet utilisateur.</p>
                    ) : (
                        <div className="space-y-3 text-sm">
                            <AiBlock icon={ThumbsUp} color="text-emerald-600" title="Ce qui est bien fait" text={detail.ai_analysis.ce_qui_est_bien} />
                            <AiBlock icon={ThumbsDown} color="text-orange-600" title="Ce qui est mal fait" text={detail.ai_analysis.ce_qui_est_mal} />
                            <AiBlock icon={AlertTriangle} color="text-red-600" title="Risques observés" text={detail.ai_analysis.risques} />
                            {detail.ai_analysis.actions_prioritaires?.length > 0 && (
                                <div>
                                    <p className="flex items-center gap-1.5 font-medium text-blue-700"><Target className="w-4 h-4" />Actions prioritaires</p>
                                    <ol className="mt-1 ml-5 list-decimal space-y-0.5 text-slate-700">
                                        {detail.ai_analysis.actions_prioritaires.map((a, i) => <li key={i}>{a}</li>)}
                                    </ol>
                                </div>
                            )}
                            {detail.ai_analysis.message_manager && (
                                <div className="rounded-lg bg-white/70 p-3 border border-violet-100">
                                    <p className="flex items-center gap-1.5 font-medium text-violet-700 mb-1"><MessageSquare className="w-4 h-4" />Message conseillé au manager</p>
                                    <p className="italic text-slate-700">"{detail.ai_analysis.message_manager}"</p>
                                </div>
                            )}
                            {detail.ai_analysis.generated_at && (
                                <p className="text-[11px] text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3" />Généré le {new Date(detail.ai_analysis.generated_at).toLocaleString('fr-FR')}</p>
                            )}
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
};

export default UserImplicationCard;
